import React, { useState, useRef, useEffect, useMemo } from 'react';
import api from '../api/axiosConfig';
import { useLanguage } from '../i18n/LanguageContext';
import { renderNoteHtml } from '../utils/noteRichContent';
import { htmlToPlainText } from '../utils/htmlToPlainText';
import { downloadMedia, formatBytes, openMedia } from '../utils/attachments';
import NoteExpansion from './NoteExpansion';
import './NoteCard.css';

interface Tag {
  id: number;
  name: string;
  color: string;
  text_color: string;
}

interface ExternalLink {
  url: string;
  link_type: string;
}

interface NoteCardProps {
  note: any;
  onEdit: (noteId: number) => void;
  onDelete: (noteId: number) => void;
  isSelectMode?: boolean;
  isSelected?: boolean;
  onSelect?: (entryId: number) => void;
  onSeeThreads?: (entryId: number) => void;
  /** Absent where sharing makes no sense (a selection grid, say) */
  onShare?: (note: any) => void;
}

const NoteCard: React.FC<NoteCardProps> = ({
  note,
  onEdit,
  onDelete,
  isSelectMode = false,
  isSelected = false,
  onSelect,
  onSeeThreads,
  onShare
}) => {
  const { t } = useLanguage();
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [imageUrls, setImageUrls] = useState<{[key: string]: string}>({});
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [copiedType, setCopiedType] = useState<'text' | 'image' | null>(null);
  // The card's rect when "view more" was clicked, which is where the
  // expansion goes; null means it is closed.
  const [expandedAt, setExpandedAt] = useState<DOMRect | null>(null);
  const [isClamped, setIsClamped] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchingRef = useRef<Set<string>>(new Set());
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  // Close menu when clicking outside or pressing Escape
  useEffect(() => {
    if (!showMenu) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showMenu]);

  // The note's own HTML rather than the server's flat preview: the preview is
  // one line with the tags stripped, which turns a stored command into a
  // single line of text and drops the rest of the block entirely.
  const bodyHtml = useMemo(
    () => renderNoteHtml(
      (note.content || '')
        .replace(/\[\[image\]\]/g, '')
        .replace(/\[\[metadata\]\]:/g, '')
    ),
    [note.content]
  );

  // "View more" appears only when there is genuinely more: the body is clamped
  // by CSS, so overflowing its own box is the honest test. Re-measured on
  // resize because a narrower card wraps more lines.
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const measure = () => setIsClamped(el.scrollHeight > el.clientHeight + 1);
    measure();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [bodyHtml]);

  // Fetch image as blob through /media/ endpoint (same as SearchModal)
  const fetchImageAsBlob = async (fileUrl: string) => {
    try {
      const filename = fileUrl.split('/').pop();
      if (!filename) {
        console.error('Could not extract filename from URL:', fileUrl);
        return null;
      }
      const cleanFilename = filename.split('?')[0];

      let blob: Blob;
      if (/^https?:\/\//.test(fileUrl)) {
        // Presigned S3/R2 URL: the query-string signature is the credential.
        // A plain fetch avoids auth headers/cookies, which would force a
        // CORS preflight and (for S3) an auth-mechanism conflict.
        const response = await fetch(fileUrl);
        if (!response.ok) {
          console.error(`Failed to fetch image ${cleanFilename}: ${response.status}`);
          return null;
        }
        blob = await response.blob();
      } else {
        // Relative path (local-storage dev): go through the API endpoint
        const response = await api.get(`/media/${cleanFilename}`, {
          responseType: 'blob',
        });
        blob = response.data;
      }

      const url = URL.createObjectURL(blob);
      setImageUrls(prev => ({ ...prev, [cleanFilename]: url }));
      return url;
    } catch (error: any) {
      console.error('Failed to fetch image:', fileUrl, error);
    }
    return null;
  };

  // Load image if note has media (same logic as SearchModal's useEffect)
  React.useEffect(() => {

    // Check if this is an image note - note.media.file indicates an image even if content preview doesn't show [[image]]
    if (note.media && note.media.file && note.media.media_type !== 'pdf') {
      // Use file_url if available (from search), otherwise construct from file name (from latest)
      const fileUrl = note.media.file_url || note.media.file;
      const filename = fileUrl?.split('/').pop()?.split('?')[0];
      
      
      if (fileUrl && filename && !imageUrls[filename] && !fetchingRef.current.has(fileUrl)) {
        fetchingRef.current.add(fileUrl);
        
        fetchImageAsBlob(fileUrl).finally(() => {
          fetchingRef.current.delete(fileUrl);
        });
      } else {
      }
    } else {
    }

    // Cleanup blob URLs on unmount
    return () => {
      Object.values(imageUrls).forEach(url => URL.revokeObjectURL(url));
    };
  }, [note, imageUrls]);

  const showCopiedFeedback = (type: 'text' | 'image') => {
    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    setCopiedType(type);
    copiedTimerRef.current = setTimeout(() => setCopiedType(null), 2000);
  };

  const handleCopy = async () => {
    try {
      // Extract filename for lookup (use file_url or file)
      const fileUrl = note.media?.file_url || note.media?.file;
      const filename = fileUrl ? fileUrl.split('/').pop()?.split('?')[0] : null;

      if (note.media && note.media.file && filename && imageUrls[filename]) {
        // Copy image - convert to PNG for better clipboard compatibility
        const response = await fetch(imageUrls[filename]);
        const blob = await response.blob();

        // Convert to PNG using canvas
        const img = new Image();
        img.src = URL.createObjectURL(blob);
        await new Promise((resolve) => { img.onload = resolve; });

        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0);

        canvas.toBlob(async (pngBlob) => {
          if (pngBlob) {
            await navigator.clipboard.write([
              new ClipboardItem({ 'image/png': pngBlob })
            ]);
            showCopiedFeedback('image');
          }
        }, 'image/png');

        URL.revokeObjectURL(img.src);
        return;
      } else {
        // Build clean HTML for rich paste (preserves lists, bold, italic in Google Docs etc.)
        let rawHtml = note.content;
        // Strip [[image]] markers
        rawHtml = rawHtml.replace(/\[\[image\]\]/g, '');

        // Plain-text fallback: keep numbered lists numbered and bullets bulleted
        // so pasting into plain-text-only fields preserves the structure.
        const plainText = htmlToPlainText(rawHtml);

        try {
          // Write both HTML and plain text so rich editors get proper formatting
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/html': new Blob([rawHtml], { type: 'text/html' }),
              'text/plain': new Blob([plainText], { type: 'text/plain' }),
            }),
          ]);
        } catch {
          // Fallback for browsers that don't support ClipboardItem with text/html
          await navigator.clipboard.writeText(plainText);
        }
        showCopiedFeedback('text');
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleImageClick = async (e: React.MouseEvent, imageUrl: string) => {
    e.stopPropagation();
    // Copy to clipboard - convert to PNG for better compatibility
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      // Convert to PNG using canvas
      const img = new Image();
      img.src = URL.createObjectURL(blob);
      await new Promise((resolve) => { img.onload = resolve; });
      
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0);
      
      canvas.toBlob(async (pngBlob) => {
        if (pngBlob) {
          await navigator.clipboard.write([
            new ClipboardItem({ 'image/png': pngBlob })
          ]);
          showCopiedFeedback('image');
        }
      }, 'image/png');

      URL.revokeObjectURL(img.src);
    } catch (err) {
      console.error('Failed to copy image:', err);
    }
    // Show full size modal
    setSelectedImage(imageUrl);
  };

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!showMenu && menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right + window.scrollX - 120 // 120 is menu width
      });
    }
    
    setShowMenu(!showMenu);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    onEdit(note.id);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    onShare?.(note);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    if (window.confirm(t.note.confirmDelete)) {
      onDelete(note.id);
    }
  };

  // const getTruncatedContent = () => {
  //   const tempDiv = document.createElement('div');
  //   tempDiv.innerHTML = note.content;
  //   const textContent = tempDiv.textContent || tempDiv.innerText || '';
  //   return textContent.length > 400 ? textContent.substring(0, 400) + '...' : textContent;
  // };

  // Check if this is an image note by presence of media.file (works for both /latest and /search)
  const isPdfNote = note.media?.media_type === 'pdf' && !!(note.media.file_url || note.media.file);
  const isImageNote = note.media && note.media.file && !isPdfNote;
  const pdfUrl: string = isPdfNote ? (note.media.file_url || note.media.file) : '';
  const pdfName: string = isPdfNote ? (note.media.name || 'document.pdf') : '';


  const handleDownloadPdf = (e: React.MouseEvent) => {
    e.stopPropagation();
    downloadMedia(pdfUrl, pdfName).catch(err => console.error('Failed to download PDF:', err));
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (isSelectMode && onSelect) {
      onSelect(note.id);
    } else if (isPdfNote) {
      // A browser cannot put a PDF on the clipboard as a file, so the click
      // that copies other notes opens this one; the arrow saves it instead.
      openMedia(pdfUrl).catch(err => console.error('Failed to open PDF:', err));
    } else {
      handleCopy();
    }
  };

  // console.log('🎨 NoteCard render:', {
  //   noteId: note.id,
  //   isImageNote,
  //   mediaFile: note.media?.file,
  //   mediaFileUrl: note.media?.file_url,
  //   imageUrlsKeys: Object.keys(imageUrls),
  //   imageUrlsValues: Object.values(imageUrls).length
  // });

  return (
    <>
    <div 
      ref={cardRef}
      className={`note-card ${isSelectMode ? 'select-mode' : ''} ${isSelected ? 'selected' : ''} ${showMenu ? 'menu-open' : ''}`}
      onClick={handleCardClick}
    >
      <div className="note-card-header">
        <span style={{ marginRight: 'auto', fontSize: '11px', color: copiedType ? '#3b82f6' : '#666' }}>
          {copiedType === 'image' ? t.note.imageCopied : copiedType === 'text' ? t.note.textCopied
            : isPdfNote ? t.note.clickToOpenPdf : t.note.clickToCopy}
        </span>
        {onSeeThreads && !isSelectMode && (
          <button
            className="thread-btn"
            title={t.note.seeThreads}
            aria-label={t.note.seeThreads}
            onClick={(e) => { e.stopPropagation(); onSeeThreads(note.id); }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
              <circle cx="6" cy="6" r="2"></circle>
              <circle cx="18" cy="8" r="2"></circle>
              <circle cx="8" cy="18" r="2"></circle>
              <circle cx="18" cy="18" r="2"></circle>
              <path d="M8 6h8M7 8l1 8M10 18h6M18 10v6" strokeDasharray="2 2"></path>
            </svg>
          </button>
        )}
        <div className="note-card-menu">
          <button ref={menuButtonRef} className="menu-btn" onClick={handleMenuClick}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="5" r="2"></circle>
              <circle cx="12" cy="12" r="2"></circle>
              <circle cx="12" cy="19" r="2"></circle>
            </svg>
          </button>
        </div>
      </div>

      <div className="note-card-body">
        {isImageNote ? (
          <>
            {(note.media.file_url || note.media.file) ? (
              // Extract clean filename for the key (same as SearchModal)
              (() => {
                const fileUrl = note.media.file_url || note.media.file;
                const filename = fileUrl.split('/').pop()?.split('?')[0];
                const hasImageUrl = filename && imageUrls[filename];
                
                // console.log('🖼️ NoteCard render image section:', {
                //   noteId: note.id,
                //   fileUrl,
                //   filename,
                //   hasImageUrl,
                //   imageUrl: hasImageUrl ? imageUrls[filename!] : null
                // });
                
                return hasImageUrl ? (
                  <>
                    <div className="note-image-container">
                      <img 
                        src={imageUrls[filename!]} 
                        alt={t.note.mediaAlt} 
                        className="note-image"
                        onClick={(e) => handleImageClick(e, imageUrls[filename!])}
                        onError={() => {
                          console.error('NoteCard: Image failed to load:', imageUrls[filename!]);
                          // Remove the failed URL so it can be retried
                          setImageUrls(prev => {
                            const newUrls = { ...prev };
                            delete newUrls[filename!];
                            return newUrls;
                          });
                        }}
                      />
                    </div>
                    {note.tag_details && note.tag_details.length > 0 && (
                      <div className="note-tags">
                        {note.tag_details.map((tag: Tag) => (
                          <span 
                            key={tag.id} 
                            className="note-tag" 
                            style={{ backgroundColor: tag.color, color: tag.text_color }}
                          >
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    )}
                    {note.links && note.links.length > 0 && (
                      <div className="note-links">
                        {note.links.map((link: ExternalLink, index: number) => (
                          <a
                            key={index}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`note-link note-link-${link.link_type}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                            </svg>
                            <span className="note-link-type">{link.link_type}</span>
                          </a>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div 
                    className="image-loading"
                    onClick={() => {
                      // Retry loading the image when clicked
                      const fileUrl = note.media.file_url || note.media.file;
                      if (fileUrl && !fetchingRef.current.has(fileUrl)) {
                        // console.log('NoteCard: Retrying image load for:', fileUrl);
                        fetchingRef.current.add(fileUrl);
                        fetchImageAsBlob(fileUrl).finally(() => {
                          fetchingRef.current.delete(fileUrl);
                        });
                      }
                    }}
                  >
                    {(note.media.file_url || note.media.file) ? t.note.loadingImage : t.note.noImageFile}
                  </div>
                );
              })()
            ) : (
              <div className="image-loading">{t.note.noImageUrl}</div>
            )}
          </>
        ) : (
          <>
            <div
              ref={contentRef}
              className={`note-content${isClamped ? ' is-clamped' : ''}`}
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />
            {isClamped && (
              <button
                type="button"
                className="note-view-more"
                onClick={(e) => {
                  e.stopPropagation();
                  const rect = cardRef.current?.getBoundingClientRect();
                  if (rect) setExpandedAt(rect);
                }}
              >
                {t.note.viewMore}
              </button>
            )}
            {isPdfNote && (
              <div className="note-pdf">
                {/* Not a button of its own: clicking anywhere on the card opens it */}
                <div className="note-pdf-file">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                  </svg>
                  <span className="note-pdf-name">{pdfName}</span>
                  {note.media.size > 0 && (
                    <span className="note-pdf-size">{formatBytes(note.media.size)}</span>
                  )}
                </div>
                <button
                  type="button"
                  className="note-pdf-download"
                  onClick={handleDownloadPdf}
                  title={t.note.downloadPdf}
                  aria-label={t.note.downloadPdf}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                </button>
              </div>
            )}
            {note.tag_details && note.tag_details.length > 0 && (
              <div className="note-tags">
                {note.tag_details.map((tag: Tag) => (
                  <span 
                    key={tag.id} 
                    className="note-tag" 
                    style={{ backgroundColor: tag.color, color: tag.text_color }}
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            )}
            {note.links && note.links.length > 0 && (
              <div className="note-links">
                {note.links.map((link: ExternalLink, index: number) => (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`note-link note-link-${link.link_type}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                    </svg>
                    <span className="note-link-type">{link.link_type}</span>
                  </a>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
    
    {expandedAt && (
      <NoteExpansion
        html={bodyHtml}
        anchor={expandedAt}
        onClose={() => setExpandedAt(null)}
        onEdit={() => { setExpandedAt(null); onEdit(note.id); }}
      />
    )}

    {selectedImage && (
      <div className="image-modal-overlay" onClick={() => setSelectedImage(null)}>
        <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setSelectedImage(null)} 
            className="image-modal-close"
            type="button"
            aria-label={t.note.close}
          >
            ×
          </button>
          <img src={selectedImage} alt={t.note.fullSizeAlt} />
        </div>
      </div>
    )}
    
    {showMenu && menuPosition && (
      <div 
        ref={menuRef}
        className="menu-dropdown-portal"
        style={{
          position: 'absolute',
          top: `${menuPosition.top}px`,
          left: `${menuPosition.left}px`,
          zIndex: 10000
        }}
      >
        <button onClick={handleEdit}>{t.note.edit}</button>
        {onShare && <button onClick={handleShare}>{t.sharing.share}</button>}
        <button onClick={handleDelete} className="delete-option">{t.note.delete}</button>
      </div>
    )}
    </>
  );
};

export default NoteCard;
